import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocalStorage } from "@uidotdev/usehooks";

const getCowImage = (breed, apiImage) => {
  // API image priority
  if (apiImage && apiImage !== "null") return apiImage;

  // Try breed image
  if (breed) {
    const formattedBreed = breed.trim();
    return `/img/breeds/${formattedBreed}.jpg`;
  }

  // final fallback
  return "/img/breeds/Dummy.png";
};



const TraceabilityDataContext = createContext(null);

const API_BASE = process.env.NODE_ENV === 'development'
  ? '/traceability/service-04/dairy-tales/'
  : 'https://knowyourmilk-uat.smartmoo.com/traceability/service-04/dairy-tales/';
const API_KEY = 'TrAceAbiLITy@^)!';
const IMAGE_HOST = 'https://knowyourmilk-uat.smartmoo.com';

export const TraceabilityDataProvider = ({ children }) => {
  const [storedBatchCode] = useLocalStorage("batchCode", null);

  // State for all API data
  const [brandData, setBrandData] = useState(null);
  const [dairyFarmMessage, setDairyFarmMessage] = useState(null);
  const [cowsData, setCowsData] = useState(null);
  const [farmersData, setFarmersData] = useState(null);

  // Loading states
  const [loading, setLoading] = useState({
    brand: false,
    dairyFarm: false,
    cows: false,
    farmers: false,
  });

  // Error states
  const [errors, setErrors] = useState({
    brand: null,
    dairyFarm: null,
    cows: null,
    farmers: null,
  });

  // Fetch Brand Logo
  const fetchBrandData = async (batchCode) => {
    if (!batchCode) return;
    setLoading(prev => ({ ...prev, brand: true }));
    setErrors(prev => ({ ...prev, brand: null }));
    try {
      const url = `${API_BASE}${encodeURIComponent(batchCode)}/brand`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'x-api-key': API_KEY }
      });
      const json = await res.json();

      // eslint-disable-next-line no-console
      console.log('Brand Logo API Response:', {
        batchCode,
        status: res.status,
        statusText: res.statusText,
        responseBody: json
      });

      if (res.ok && json && json.statusCode === 200 && json.data) {
        setBrandData(json.data);
      } else {
        setErrors(prev => ({ ...prev, brand: json?.message || 'Failed to fetch brand data' }));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Brand Logo fetch error', e);
      setErrors(prev => ({ ...prev, brand: 'Network error loading brand data' }));
    } finally {
      setLoading(prev => ({ ...prev, brand: false }));
    }
  };

  // Fetch Dairy Farm Message
  const fetchDairyFarmMessage = async (batchCode) => {
    if (!batchCode) return;
    setLoading(prev => ({ ...prev, dairyFarm: true }));
    setErrors(prev => ({ ...prev, dairyFarm: null }));
    try {
      const url = `${API_BASE}${encodeURIComponent(batchCode)}/dairy-farm-message`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'x-api-key': API_KEY }
      });
      const json = await res.json();

      // eslint-disable-next-line no-console
      console.log('Dairy Farm Message API Response:', {
        batchCode,
        status: res.status,
        statusText: res.statusText,
        responseBody: json
      });

      if (res.ok && json && json.statusCode === 200 && json.data) {
        setDairyFarmMessage(json.data);
      } else {
        setErrors(prev => ({ ...prev, dairyFarm: json?.message || 'Failed to fetch dairy farm message' }));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Dairy Farm Message fetch error', e);
      setErrors(prev => ({ ...prev, dairyFarm: 'Network error loading dairy farm message' }));
    } finally {
      setLoading(prev => ({ ...prev, dairyFarm: false }));
    }
  };

  // Fetch Meet Our Cows
  const fetchCowsData = async (batchCode, page = 0, records = 2000) => {
    if (!batchCode) return;
    setLoading(prev => ({ ...prev, cows: true }));
    setErrors(prev => ({ ...prev, cows: null }));
    try {
      const url = `${API_BASE}${encodeURIComponent(batchCode)}/meet-our-cow/page/${page}/records/${records}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'x-api-key': API_KEY }
      });
      const json = await res.json();

      // eslint-disable-next-line no-console
      console.log('Meet Our Cow API Response:', {
        batchCode,
        status: res.status,
        statusText: res.statusText,
        responseBody: json
      });

      if (res.ok && json && json.statusCode === 200 && json.data) {
        // Map cows data with proper field extraction
        const mappedCows = (json.data.cows || []).map((c) => {
          // Extract health details
          const healthDetailsRaw = c.health?.["Health Details"] || c.health?.healthDetails || {};
          const healthDetails = {
            vaccinated: healthDetailsRaw["Vaccinated"] || healthDetailsRaw.vaccinated || null,
            dewormed: healthDetailsRaw["Dewormed"] || healthDetailsRaw.dewormed || null,
            lastCheckup: healthDetailsRaw["Last Checkup"] || healthDetailsRaw.lastCheckup || null,
          };

          // Extract farmer details
          const farmerDetailsRaw = c.farmer?.["Farmer Details"] || c.farmer?.farmerDetails || {};
          const farmerDetails = {
            farmerName: farmerDetailsRaw["Farmer's Name"] || farmerDetailsRaw["Farmer Name"] || farmerDetailsRaw.farmerName || null,
            farmerAt: farmerDetailsRaw["Farmer at"] || farmerDetailsRaw.farmerAt || null,
          };

          return {
            id: c.id,
            name: c.name || c.breed || `Cow`,
            regNo: c.reg_no || c.regNo || "-",
            breed: c.breed || "-",
            dob: c.dob || "-",
            about: c.descr || "",
            healthObj: healthDetails,
            farmerObj: farmerDetails,
            img: getCowImage(c.breed, c.image),

          };
        });

        setCowsData({
          ...json.data,
          mappedCows,
        });
      } else {
        setErrors(prev => ({ ...prev, cows: json?.message || 'Failed to fetch cows data' }));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Meet Our Cow fetch error', e);
      setErrors(prev => ({ ...prev, cows: 'Network error loading cows' }));
    } finally {
      setLoading(prev => ({ ...prev, cows: false }));
    }
  };

  // Fetch Meet Our Farmers
  const fetchFarmersData = async (batchCode, page = 0, records = 2000) => {
    if (!batchCode) return;
    setLoading(prev => ({ ...prev, farmers: true }));
    setErrors(prev => ({ ...prev, farmers: null }));
    try {
      const url = `${API_BASE}${encodeURIComponent(batchCode)}/meet-our-farmers/page/${page}/records/${records}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'x-api-key': API_KEY }
      });
      const json = await res.json();

      // eslint-disable-next-line no-console
      console.log('Meet Our Farmers API Response:', {
        batchCode,
        status: res.status,
        statusText: res.statusText,
        responseBody: json
      });

      if (res.ok && json && json.statusCode === 200 && json.data) {
        // Map farmers data
        const mappedFarmers = (json.data.farmers || []).map((f, idx) => {
          const farmerId = f.id || `farmer-${idx + 1}`;
          return {
            id: farmerId,
            name: f.name || "Farmer",
            registrationNo: f.registrationNo || f.registration_no || f.regNo || f.reg_no || "-",
            dob: f.dob || f.dateOfBirth || "-",
            bio: f.bio || f.description || f.descr || "",
            image: f.image ? `${f.image}` : "img/f1.png",
            cows: (f.cows || []).map((c, cowIdx) => ({
              id: c.id || `farmer-${farmerId}-cow-${cowIdx}`,
              name: c.name || c.breed || "Cow",
              regNo: c.regNo || c.reg_no || "-",
              breed: c.breed || "-",
              dob: c.dob || "-",
              img: c.image ? `${c.image}` : "img/c1-min.png",
            })),
          };
        });

        setFarmersData({
          ...json.data,
          mappedFarmers,
        });
      } else {
        setErrors(prev => ({ ...prev, farmers: json?.message || 'Failed to fetch farmers data' }));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Meet Our Farmers fetch error', e);
      setErrors(prev => ({ ...prev, farmers: 'Network error loading farmers' }));
    } finally {
      setLoading(prev => ({ ...prev, farmers: false }));
    }
  };

  // Fetch all data when batch code changes
  useEffect(() => {
    const code = (storedBatchCode || "").trim();
    if (!code) return;

    // Fetch all data in parallel
    fetchBrandData(code);
    fetchDairyFarmMessage(code);
    fetchCowsData(code);
    fetchFarmersData(code);
  }, [storedBatchCode]);

  const value = {
    // Data
    brandData,
    dairyFarmMessage,
    cowsData,
    farmersData,

    // Loading states
    loading,

    // Errors
    errors,

    // Refetch functions
    refetchBrand: () => fetchBrandData((storedBatchCode || "").trim()),
    refetchDairyFarm: () => fetchDairyFarmMessage((storedBatchCode || "").trim()),
    refetchCows: () => fetchCowsData((storedBatchCode || "").trim()),
    refetchFarmers: () => fetchFarmersData((storedBatchCode || "").trim()),
    refetchAll: () => {
      const code = (storedBatchCode || "").trim();
      if (code) {
        fetchBrandData(code);
        fetchDairyFarmMessage(code);
        fetchCowsData(code);
        fetchFarmersData(code);
      }
    },
  };

  return (
    <TraceabilityDataContext.Provider value={value}>
      {children}
    </TraceabilityDataContext.Provider>
  );
};

export const useTraceabilityData = () => {
  const context = useContext(TraceabilityDataContext);
  if (!context) {
    throw new Error('useTraceabilityData must be used within TraceabilityDataProvider');
  }
  return context;
};

